/**
 * @file EmptyState.tsx
 * @description Dedicated empty-state block — replaces the "אין X" plain
 *   <p>'s scattered across list pages. Shows an emoji-ish icon, a
 *   heading, a one-line description, and an optional CTA button.
 *   Centered, padded, and same RTL-friendly layout in both locales.
 * @module Components/Common
 * @security Renders pre-translated strings only; no dangerouslySetInnerHTML.
 *   The CTA's onClick should perform a routine app action (navigate or
 *   open a form) — never expose unsafe behavior here.
 */

import type { JSX, ReactNode } from 'react';
import { Button } from './Button';

interface EmptyStateProps {
  /** Optional pictogram/emoji string OR a small ReactNode (e.g. an SVG). */
  icon?: ReactNode;
  title: string;
  description?: string;
  cta?: {
    label: string;
    onClick: () => void;
  };
}

export function EmptyState({ icon, title, description, cta }: EmptyStateProps): JSX.Element {
  return (
    <div className="flex flex-col items-center justify-center text-center gap-3 py-12 px-4 border border-dashed border-slate-300/80 rounded-xl bg-white/60">
      {icon !== undefined ? (
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-soft text-2xl leading-none shadow-card">
          {icon}
        </div>
      ) : null}
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      {description !== undefined ? (
        <p className="text-sm text-gray-600 max-w-prose">{description}</p>
      ) : null}
      {cta !== undefined ? (
        <Button type="button" onClick={cta.onClick} className="mt-2">
          {cta.label}
        </Button>
      ) : null}
    </div>
  );
}
